import { ISharedDelivery, ISharedSitePermit } from '~/client/graph'
import { FALLBACK_WORKFLOW_ICON_NAME } from '~/client/src/shared/constants/workflowTypeGlyphs'

import { DELIVERY_PIN_ICON_NAME } from './deliveryPinPoints'
import { pinCoordKey } from './pinCoordKey'
import { IWorkflowPinPoint } from './workflowPinPoints'

// The shared DTO packs its coordinates into one query value.
const LOCATION_QUERY_SEPARATOR = ','
// The shared DTO carries its location as a maps link rather than coordinates.
const LOCATION_QUERY_PARAM = 'q'

export enum SharedEntityKind {
  Workflow = 'workflow',
  Delivery = 'delivery',
}

export interface ISharedPinGroup {
  kind: SharedEntityKind
  workflows: ISharedSitePermit[]
  deliveries: ISharedDelivery[]
}

export interface ISharedPinPointsOptions {
  // The coordinate key rather than the group: this builder runs inside a computed, so every
  // recompute hands out fresh group objects and an identity comparison would stop matching the
  // moment anything upstream changed.
  selectedCoordKey?: string
}

interface ICoordinates {
  latitude: number
  longitude: number
}

// `...?q=<lat>,<lng>`. A missing or malformed link means the entity has nowhere to draw —
// today that throws inside render, so unparseable returns null and the entity is skipped.
export function parseSharedLocation(locationUrl: string): ICoordinates | null {
  if (!locationUrl) {
    return null
  }

  let query: string
  try {
    query = new URL(locationUrl).searchParams.get(LOCATION_QUERY_PARAM)
  } catch {
    return null
  }

  const [latitude, longitude] = (query || '')
    .split(LOCATION_QUERY_SEPARATOR)
    .map(Number)

  return Number.isFinite(latitude) && Number.isFinite(longitude)
    ? { latitude, longitude }
    : null
}

// One pin per coordinate per kind, so several entities sharing a point collapse into a count
// bubble instead of stacking invisibly — the DOM markers drew all of them superimposed, which
// left every one but the topmost unreachable. Workflows and deliveries stay separate groups:
// a pin carries one glyph, and merging them would have to drop one.
export function buildSharedPinPoints(
  workflows: ISharedSitePermit[],
  deliveries: ISharedDelivery[],
  options: ISharedPinPointsOptions = {},
): {
  points: IWorkflowPinPoint[]
  groupsByCoordKey: Map<string, ISharedPinGroup>
} {
  const groupsByCoordKey = new Map<string, ISharedPinGroup>()
  const coordsByKey = new Map<string, ICoordinates>()

  const collect = (
    kind: SharedEntityKind,
    locationUrl: string,
    add: (group: ISharedPinGroup) => void,
  ): void => {
    const coord = parseSharedLocation(locationUrl)
    if (!coord) {
      return
    }

    const coordKey = pinCoordKey(coord, kind)
    if (!groupsByCoordKey.has(coordKey)) {
      groupsByCoordKey.set(coordKey, { kind, workflows: [], deliveries: [] })
      coordsByKey.set(coordKey, coord)
    }
    add(groupsByCoordKey.get(coordKey))
  }

  for (const workflow of workflows) {
    collect(SharedEntityKind.Workflow, workflow.locationUrl, group =>
      group.workflows.push(workflow),
    )
  }

  for (const delivery of deliveries) {
    collect(
      SharedEntityKind.Delivery,
      delivery.locationsTo?.[0]?.locationUrl,
      group => group.deliveries.push(delivery),
    )
  }

  const points: IWorkflowPinPoint[] = []
  for (const [coordKey, group] of groupsByCoordKey) {
    const { latitude, longitude } = coordsByKey.get(coordKey)
    points.push({
      coordKey,
      latitude,
      longitude,
      count: group.workflows.length + group.deliveries.length,
      iconName:
        group.kind === SharedEntityKind.Delivery
          ? DELIVERY_PIN_ICON_NAME
          : FALLBACK_WORKFLOW_ICON_NAME,
      isSelected: coordKey === options.selectedCoordKey,
      // No status is passed, and none would be read if it were: a shared entity carries a
      // status from its own project, not one this project can act on, so isShared alone
      // decides the fill — the closed grey, which the chip below the pin follows.
      isDone: false,
      isAssigned: false,
      isShared: true,
      // Shared entities sit at their own coordinate with no marker of this project's
      // beneath them, so there is nothing to lift clear of.
      anchored: false,
    })
  }

  return { points, groupsByCoordKey }
}
